<script setup lang="ts">
/**
 * ViewSwitcherDropdown
 *
 * Dropdown list of saved views organized by My Views, Shared, System.
 * Includes search, type/visibility icons, count badges, inline rename.
 */

import { ref, computed, nextTick, watch } from 'vue'
import type { SavedView } from '../api/types'

interface Props {
    myViews: SavedView[]
    sharedViews: SavedView[]
    systemViews: SavedView[]
    activeViewId?: string | null
    counts?: Map<string, number>
    /** ID of the view currently being renamed (null = none) */
    renamingViewId?: string | null
    /** Current rename input value */
    renameValue?: string
}

const props = withDefaults(defineProps<Props>(), {
    activeViewId: null,
    counts: () => new Map(),
    renamingViewId: null,
    renameValue: '',
})

const emit = defineEmits<{
    select: [viewId: string]
    clear: []
    create: []
    'open-actions': [view: SavedView, event: MouseEvent]
    'rename-submit': [viewId: string, newName: string]
    'rename-cancel': []
    'update:rename-value': [value: string]
    'toggle-default': [view: SavedView]
}>()

const searchQuery = ref('')
const renameInputRef = ref<HTMLInputElement | null>(null)

const allViews = computed(() => [
    ...props.myViews,
    ...props.sharedViews,
    ...props.systemViews,
])

const filteredMyViews = computed(() => filterViews(props.myViews))
const filteredSharedViews = computed(() => filterViews(props.sharedViews))
const filteredSystemViews = computed(() => filterViews(props.systemViews))

function filterViews(views: SavedView[]): SavedView[] {
    const q = searchQuery.value.toLowerCase().trim()
    if (!q) return views
    return views.filter(v => v.name.toLowerCase().includes(q))
}

function viewTypeIcon(view: SavedView): string {
    if (view.visibility === 'system') return '🔒'
    if (view.type === 'static') return '📌'
    if (view.visibility === 'shared') return '👥'
    return '🔍'
}

function getCount(viewId: string): number | null {
    return props.counts.get(viewId) ?? null
}

function onRenameKeydown(e: KeyboardEvent) {
    if (e.key === 'Enter') {
        e.preventDefault()
        emit('rename-submit', props.renamingViewId!, props.renameValue)
    } else if (e.key === 'Escape') {
        e.preventDefault()
        emit('rename-cancel')
    }
}

function onRenameBlur() {
    // Submit on blur — if the name is non-empty, save it
    if (props.renamingViewId) {
        emit('rename-submit', props.renamingViewId, props.renameValue)
    }
}

// Auto-focus the rename input when it appears
watch(() => props.renamingViewId, async (newId) => {
    if (newId) {
        await nextTick()
        renameInputRef.value?.focus()
        renameInputRef.value?.select()
    }
})

function onKeydown(e: KeyboardEvent) {
    if (e.key === 'Escape' && !props.renamingViewId) emit('clear')
}
</script>

<template>
    <div class="view-switcher" @keydown="onKeydown" data-testid="view-switcher-dropdown">
        <!-- Search -->
        <div class="view-switcher__search">
            <input
                v-model="searchQuery"
                type="text"
                class="view-switcher__search-input"
                data-testid="view-switcher-search"
                placeholder="Search views…"
                :autofocus="!renamingViewId"
            />
        </div>

        <!-- All (reset) -->
        <button
            :class="['view-switcher__item', { 'view-switcher__item--active': !activeViewId }]"
            @click="emit('clear')"
        >
            <span class="view-switcher__icon">📋</span>
            <span class="view-switcher__name">All</span>
        </button>

        <!-- My Views -->
        <template v-if="filteredMyViews.length > 0">
            <div class="view-switcher__section-label">My Views</div>
            <div v-for="view in filteredMyViews" :key="view.id">
                <!-- Inline Rename Mode -->
                <div v-if="renamingViewId === view.id" class="view-switcher__rename-row">
                    <span class="view-switcher__icon">{{ viewTypeIcon(view) }}</span>
                    <input
                        ref="renameInputRef"
                        class="view-switcher__rename-input"
                        :value="renameValue"
                        @input="emit('update:rename-value', ($event.target as HTMLInputElement).value)"
                        @keydown="onRenameKeydown"
                        @blur="onRenameBlur"
                    />
                    <button
                        class="view-switcher__rename-confirm"
                        title="Save"
                        @mousedown.prevent="emit('rename-submit', view.id, renameValue)"
                    >
                        ✓
                    </button>
                </div>

                <!-- Normal Display Mode -->
                <div
                    v-else
                    :class="['view-switcher__item', { 'view-switcher__item--active': activeViewId === view.id }]"
                    @click="emit('select', view.id)"
                >
                    <span class="view-switcher__icon">{{ viewTypeIcon(view) }}</span>
                    <span class="view-switcher__name">{{ view.name }}</span>
                    <button
                        type="button"
                        :class="['view-switcher__default-btn', { 'view-switcher__default-btn--active': view.isDefault }]"
                        @click.stop="emit('toggle-default', view)"
                        :title="view.isDefault ? 'Default View (click to remove preference)' : 'Set as Default View'"
                    >
                        {{ view.isDefault ? '★' : '☆' }}
                    </button>
                    <span v-if="getCount(view.id) !== null" class="view-switcher__badge">
                        {{ getCount(view.id) }}
                    </span>
                    <button
                        type="button"
                        class="view-switcher__actions-btn"
                        @click.stop="emit('open-actions', view, $event)"
                        title="Actions"
                    >
                        ⋯
                    </button>
                </div>
            </div>
        </template>

        <!-- Shared Views -->
        <template v-if="filteredSharedViews.length > 0">
            <div class="view-switcher__section-label">Shared</div>
            <div v-for="view in filteredSharedViews" :key="view.id">
                <!-- Inline Rename Mode -->
                <div v-if="renamingViewId === view.id" class="view-switcher__rename-row">
                    <span class="view-switcher__icon">{{ viewTypeIcon(view) }}</span>
                    <input
                        ref="renameInputRef"
                        class="view-switcher__rename-input"
                        :value="renameValue"
                        @input="emit('update:rename-value', ($event.target as HTMLInputElement).value)"
                        @keydown="onRenameKeydown"
                        @blur="onRenameBlur"
                    />
                    <button
                        class="view-switcher__rename-confirm"
                        title="Save"
                        @mousedown.prevent="emit('rename-submit', view.id, renameValue)"
                    >
                        ✓
                    </button>
                </div>

                <!-- Normal Display Mode -->
                <div
                    v-else
                    :class="['view-switcher__item', { 'view-switcher__item--active': activeViewId === view.id }]"
                    @click="emit('select', view.id)"
                >
                    <span class="view-switcher__icon">{{ viewTypeIcon(view) }}</span>
                    <span class="view-switcher__name">{{ view.name }}</span>
                    <button
                        type="button"
                        :class="['view-switcher__default-btn', { 'view-switcher__default-btn--active': view.isDefault }]"
                        @click.stop="emit('toggle-default', view)"
                        :title="view.isDefault ? 'Default View (click to remove preference)' : 'Set as Default View'"
                    >
                        {{ view.isDefault ? '★' : '☆' }}
                    </button>
                    <span v-if="getCount(view.id) !== null" class="view-switcher__badge">
                        {{ getCount(view.id) }}
                    </span>
                    <button
                        type="button"
                        class="view-switcher__actions-btn"
                        @click.stop="emit('open-actions', view, $event)"
                        title="Actions"
                    >
                        ⋯
                    </button>
                </div>
            </div>
        </template>

        <!-- System Views (read-only, can set as default) -->
        <template v-if="filteredSystemViews.length > 0">
            <div class="view-switcher__section-label">System</div>
            <div
                v-for="view in filteredSystemViews"
                :key="view.id"
                :class="['view-switcher__item', { 'view-switcher__item--active': activeViewId === view.id }]"
                @click="emit('select', view.id)"
            >
                <span class="view-switcher__icon">{{ viewTypeIcon(view) }}</span>
                <span class="view-switcher__name">{{ view.name }}</span>
                <button
                    type="button"
                    :class="['view-switcher__default-btn', { 'view-switcher__default-btn--active': view.isDefault }]"
                    @click.stop="emit('toggle-default', view)"
                    :title="view.isDefault ? 'Default View (click to remove preference)' : 'Set as Default View'"
                >
                    {{ view.isDefault ? '★' : '☆' }}
                </button>
                <span v-if="getCount(view.id) !== null" class="view-switcher__badge">
                    {{ getCount(view.id) }}
                </span>
            </div>
        </template>

        <!-- Empty State -->
        <div v-if="allViews.length === 0" class="view-switcher__empty">
            No saved views yet
        </div>

        <!-- Create New -->
        <div class="view-switcher__footer">
            <button class="view-switcher__create" @click="emit('create')">
                + Create New View
            </button>
        </div>
    </div>
</template>

<style scoped>
.view-switcher {
    width: 320px;
    max-height: 480px;
    overflow-y: auto;
    background: var(--bg-elevated);
    border: 1px solid var(--border-color);
    border-radius: var(--radius-lg);
    box-shadow: var(--shadow-lg);
}

.view-switcher__search {
    padding: 0.5rem;
    border-bottom: 1px solid var(--border-color);
}

.view-switcher__search-input {
    width: 100%;
    padding: 0.375rem 0.5rem;
    font-size: var(--text-sm);
    color: var(--text-primary);
    background: var(--bg-surface);
    border: 1px solid var(--border-color);
    border-radius: var(--radius-md);
}

.view-switcher__search-input:focus {
    border-color: var(--color-primary);
    outline: none;
}

.view-switcher__section-label {
    padding: 0.5rem 0.75rem 0.25rem;
    font-size: var(--text-xs);
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--text-muted);
}

.view-switcher__item {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    width: 100%;
    padding: 0.5rem 0.75rem;
    font-size: var(--text-sm);
    color: var(--text-primary);
    background: none;
    border: none;
    cursor: pointer;
    transition: background 0.1s;
    text-align: left;
}

.view-switcher__item:hover {
    background: var(--bg-hover);
}

.view-switcher__item--active {
    border-left: 3px solid var(--color-primary);
    background: rgba(18, 192, 221, 0.05);
}

.view-switcher__icon {
    font-size: 0.875rem;
    width: 20px;
    text-align: center;
    flex-shrink: 0;
}

.view-switcher__name {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.view-switcher__default-btn {
    opacity: 0.4;
    font-size: 0.875rem;
    color: var(--text-muted);
    background: none;
    border: none;
    padding: 0 0.25rem;
    border-radius: var(--radius-sm);
    cursor: pointer;
    transition: all 0.15s;
}

.view-switcher__item:hover .view-switcher__default-btn,
.view-switcher__default-btn--active {
    opacity: 1;
}

.view-switcher__default-btn--active {
    color: #f59e0b;
}

.view-switcher__default-btn:hover {
    color: #f59e0b;
    transform: scale(1.15);
}

.view-switcher__badge {
    font-size: var(--text-xs);
    color: var(--text-muted);
    background: var(--bg-surface);
    padding: 0.125rem 0.375rem;
    border-radius: var(--radius-full);
    min-width: 24px;
    text-align: center;
}

.view-switcher__actions-btn {
    opacity: 0;
    font-size: var(--text-sm);
    color: var(--text-muted);
    background: none;
    border: none;
    padding: 0.125rem 0.25rem;
    border-radius: var(--radius-sm);
    cursor: pointer;
    transition: opacity 0.1s;
}

.view-switcher__item:hover .view-switcher__actions-btn {
    opacity: 1;
}

.view-switcher__actions-btn:hover {
    background: var(--bg-surface);
    color: var(--text-primary);
}

/* Inline Rename */
.view-switcher__rename-row {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.375rem 0.75rem;
    background: rgba(18, 192, 221, 0.05);
}

.view-switcher__rename-input {
    flex: 1;
    padding: 0.25rem 0.375rem;
    font-size: var(--text-sm);
    color: var(--text-primary);
    background: var(--bg-surface);
    border: 2px solid var(--color-primary);
    border-radius: var(--radius-sm);
    outline: none;
}

.view-switcher__rename-confirm {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    font-size: 0.75rem;
    color: #fff;
    background: var(--color-primary);
    border: none;
    border-radius: var(--radius-sm);
    cursor: pointer;
    flex-shrink: 0;
}

.view-switcher__rename-confirm:hover {
    opacity: 0.9;
}

.view-switcher__empty {
    padding: 1.5rem;
    text-align: center;
    font-size: var(--text-sm);
    color: var(--text-muted);
}

.view-switcher__footer {
    padding: 0.5rem 0.75rem;
    border-top: 1px solid var(--border-color);
}

.view-switcher__create {
    width: 100%;
    padding: 0.375rem;
    font-size: var(--text-sm);
    font-weight: 600;
    color: var(--color-primary);
    background: none;
    border: 1px dashed var(--border-color);
    border-radius: var(--radius-md);
    cursor: pointer;
    text-align: center;
}

.view-switcher__create:hover {
    border-color: var(--color-primary);
    background: rgba(18, 192, 221, 0.05);
}
</style>
